// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import askQuestionHandler from "@pacific/App/Handlers/AskQuestion/AskQuestionHandler";
import textMasks from "@pacific/App/Libraries/react-text-mask";
import { FormInputWrapper } from "@pacific/MobiusOverrides/FormField/FormField";
import { sharedDateTimeOptions } from "@pacific/XDL/constants";
import { CustomSessoinModel } from "@pacific/XDL/types";
import { getOrderNumber, xScreen } from "@pacific/XDL/Utilities";
import { modalButtonsActionStyles } from "@pacific/XDL/Utilities/sharedStyles";
import { maxLengthAttr, xValidation } from "@pacific/XDL/Utilities/validation";
import React, { RefObject, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isOpen?: boolean;
    order: OrderModel;
    onClose: (event?: React.SyntheticEvent<Element, Event>) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    session: getSession(state) as CustomSessoinModel,
});

const mapDispatchToProps = {
    askQuestionHandler,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface AskQuestionModalStyles {
    modal?: ModalPresentationProps;
    buttonsWrapper?: InjectableCss;
    modalCancelButton?: ButtonPresentationProps;
    modalSubmitButton?: ButtonPresentationProps;
    modalInput?: TextFieldProps;
    question?: TextAreaProps;
    orderInfoWrapper?: InjectableCss;
    orderInfoItem?: InjectableCss;
    dateField?: DatePickerPresentationProps;
    reCaptchaWrapper?: InjectableCss;
}

export const askQuestionModalStyles: AskQuestionModalStyles = {
    modal: {
        sizeVariant: "large",
    },
    buttonsWrapper: {
        ...modalButtonsActionStyles.buttonsWrapper,
        css: css`
            ${modalButtonsActionStyles.buttonsWrapper?.css}
            padding-top: 16px;
        `,
    },
    modalCancelButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
    modalSubmitButton: {
        ...modalButtonsActionStyles.submitButton,
    },
    modalInput: {
        cssOverrides: {
            formField: css`
                padding-bottom: 16px;
            `,
        },
    },
    question: {
        cssOverrides: {
            formField: css`
                padding-bottom: 16px;
                ${FormInputWrapper} textarea:focus {
                    padding: 9px;
                }
            `,
        },
    },
    orderInfoWrapper: {
        css: css`
            display: flex;
            gap: 0 16px;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    orderInfoItem: {
        css: css`
            width: calc(100% / 3);
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    dateField: {
        cssOverrides: {
            datePicker: css`
                .react-calendar {
                    bottom: 40px;
                    top: unset;
                }
            `,
            formField: css`
                width: 50%;
                ${xScreen("lte", "sm")} {
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                &&&& {
                    .react-datetime-picker__clear-button {
                        padding-right: 0;
                    }
                }
            `,
        },
    },
    reCaptchaWrapper: {
        css: css`
            width: 100%;
            margin-top: 16px;
            &:empty {
                display: none;
            }
        `,
    },
};

const styles = askQuestionModalStyles;

const AskQuestionModal = (props: Props) => {
    const toasterContext = useContext(ToasterContext);

    const askQuestionErrorMessage = siteMessage("Custom_Ask_Question_Modal_Error_Message");
    const phoneFieldMessage = siteMessage("Custom_Ask_Question_Modal_PhoneNumberIsInvalidErrorMessage");
    const phoneRequiredFieldMessage = siteMessage("Custom_Ask_Question_Modal_PhoneNumberIsRequiredErrorMessage");
    const questionRequiredFieldMessage = siteMessage("Custom_Ask_Question_Modal_QuestionIsRequiredErrorMessage");
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [message, setMessage] = useState("");
    const [messageError, setMessageError] = useState(questionRequiredFieldMessage);

    const [phone, setPhone] = useState(props.session.phone || "");
    const [phoneError, setPhoneError] = useState(props.session.phone ? "" : phoneRequiredFieldMessage);

    const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(
        props.order.requestedDeliveryDateDisplay ? new Date(props.order.requestedDeliveryDateDisplay) : undefined,
    );

    const modalCloseHandler = (event: React.SyntheticEvent<Element, Event>) => {
        props.onClose(event);
        resetState();
    };

    const resetState = () => {
        setMessage("");
        setPhone(props.session.phone || "");
        setRequestedDeliveryDate(
            props.order.requestedDeliveryDateDisplay ? new Date(props.order.requestedDeliveryDateDisplay) : undefined,
        );
        setIsSubmitting(false);
        setIsSubmitted(false);
    };

    const validateMessage = (message: string) => {
        const errorMessage = !message ? questionRequiredFieldMessage : "";
        setMessageError(errorMessage);
        return !errorMessage;
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        validateMessage(event.target.value);
        setMessage(event.target.value);
    };

    const validatePhone = (phone: string) => {
        const errorMessage = !phone ? phoneRequiredFieldMessage : xValidation.phone(phone) ? "" : phoneFieldMessage;
        setPhoneError(errorMessage);
        return !errorMessage;
    };

    const phoneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        validatePhone(event.target.value);
        event.target.value = textMasks.phone(event.target.value, phone || "");
        setPhone(event.target.value);
    };

    const requestedDeliveryDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setRequestedDeliveryDate(selectedDay);
    };

    const submitFormHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateMessage(message) || !validatePhone(phone)) {
            return;
        }

        const isReCaptchaValid = await props.validateReCaptcha({ location: "AskQuestion", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        props.askQuestionHandler({
            message,
            orderNumber: getOrderNumber(props.order.erpOrderNumber, props.order.webOrderNumber),
            userPhone: phone,
            requestedDeliveryDate,
            onError: (e: any) => {
                setIsSubmitting(false);
                props.onClose();
                resetState();
                toasterContext.addToast({ body: e || askQuestionErrorMessage, messageType: "danger" });
            },
            onSuccess: () => {
                setIsSubmitting(false);
                setIsSubmitted(false);
                toasterContext.addToast({
                    body: siteMessage("Custom_AskQuestion_SuccessMessage"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
                props.onClose();
                resetState();
            },
        });
    };

    const StyledForm = getStyledWrapper("form");
    const orderDate =
        getLocalizedDateTime({
            dateTime: new Date(props.order.orderDate),
            language: props.session.language,
            options: {
                ...sharedDateTimeOptions,
            },
        }) || "";
    const orderNumber = getOrderNumber(props.order.erpOrderNumber, props.order.webOrderNumber);
    return (
        <Modal
            headline={siteMessage("Custom_Ask_Question_Modal_Title")}
            isOpen={props.isOpen}
            handleClose={modalCloseHandler}
            {...styles.modal}
        >
            <StyledForm onSubmit={submitFormHandler} noValidate>
                {props.order.btCompanyName && (
                    <TextField
                        {...styles.modalInput}
                        type="text"
                        label={translate("Company Name")}
                        disabled={true}
                        value={props.order.btCompanyName}
                    />
                )}

                <StyledWrapper {...styles.orderInfoWrapper}>
                    {props.order.customerNumber && (
                        <StyledWrapper {...styles.orderInfoItem}>
                            <TextField
                                {...styles.modalInput}
                                type="text"
                                label={translate("Customer Number")}
                                disabled={true}
                                value={props.order.customerNumber}
                            />
                        </StyledWrapper>
                    )}

                    {orderNumber && (
                        <StyledWrapper {...styles.orderInfoItem}>
                            <TextField
                                {...styles.modalInput}
                                type="text"
                                label={translate("Order #")}
                                disabled={true}
                                value={orderNumber}
                            />
                        </StyledWrapper>
                    )}

                    {props.order.orderDate && (
                        <StyledWrapper {...styles.orderInfoItem}>
                            <TextField
                                {...styles.modalInput}
                                type="text"
                                label={translate("Order Date")}
                                value={orderDate}
                                disabled={true}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>

                {(props.session.firstName || props.session.lastName) && (
                    <TextField
                        {...styles.modalInput}
                        type="text"
                        label={translate("User Name")}
                        value={`${props.session.firstName} ${props.session.lastName}`}
                        disabled={true}
                        maxLength={maxLengthAttr.userName}
                    />
                )}

                {props.session.email && (
                    <TextField
                        {...styles.modalInput}
                        type="text"
                        label={translate("Email Address")}
                        value={props.session.email}
                        disabled={true}
                        maxLength={maxLengthAttr.email}
                    />
                )}

                <TextField
                    {...styles.modalInput}
                    type="text"
                    label={translate("Phone Number")}
                    value={phone}
                    required
                    onChange={phoneChangeHandler}
                    error={isSubmitted && phoneError}
                    maxLength={maxLengthAttr.phone}
                />

                <TextArea
                    {...styles.question}
                    label={translate("Message")}
                    onChange={messageChangeHandler}
                    value={message}
                    required
                    error={isSubmitted && messageError}
                />

                {props.order.notes && (
                    <TextArea
                        {...styles.question}
                        label={translate("Notes")}
                        value={props.order.notes}
                        disabled={true}
                    />
                )}

                <DatePicker
                    {...styles.dateField}
                    label={translate("Requested Delivery Date")}
                    selectedDay={requestedDeliveryDate}
                    onDayChange={requestedDeliveryDateChangeHandler}
                />

                <StyledWrapper {...styles.reCaptchaWrapper}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="AskQuestionModal" />
                </StyledWrapper>

                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button onClick={modalCloseHandler} {...styles.modalCancelButton} aria-label="Cancel">
                        {translate("Cancel")}
                    </Button>
                    <Button type="submit" disabled={isSubmitting} {...styles.modalSubmitButton} aria-label="Send">
                        {translate("Send")}
                    </Button>
                </StyledWrapper>
            </StyledForm>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(AskQuestionModal));
